import { createFinancialOperations } from '../../shared/financialOperations'
/**
 * Arquivo: src/services/financeiro.ts
 * Versão: 6.0.0
 * Data: 08/10/2026
 */
import { dataClient } from './dataClient'
import { apiJson } from './api'
import { settlementFields } from '../utils/financialPresentation'
import { obterMinhaIdentidade } from './auth'
import type { PagamentoFaturaCartao } from '../utils/faturasCartao'
import type {
  AcessoBase,
  BaseFinanceira,
  Categoria,
  CartaoCredito,
  Conta,
  Lancamento,
  PerfilAcesso,
  Subcategoria,
  TipoLancamento,
  ContaComSaldo,
  ExtratoContaItem,
  Investimento,
  InvestimentoComSaldo,
  MovimentoInvestimento,
  PosicaoInvestimento,
  Transferencia,
  AuditoriaEvento,
} from '../types/database'

export async function obterBaseAtiva(): Promise<BaseFinanceira | null> {
  const { data, error } = await dataClient
    .from('bases')
    .select('id,nome,ativa,criado_em')
    .eq('ativa', true)
    .order('criado_em', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data as BaseFinanceira | null
}

export async function obterAcessoBase(baseId: string): Promise<AcessoBase | null> {
  const info=await obterMinhaIdentidade(baseId)
  if(!info.member?.ativo)return null
  const codigos=(info.roles||[]).map((r)=>String(r.code||'').toUpperCase())
  const perfil:PerfilAcesso=codigos.includes('ADMIN')?'ADMIN':codigos.includes('LANCADOR')?'LANCADOR':'CONSULTA'
  return {base_id:baseId,user_id:info.id,perfil,ativo:true,roles:codigos,permissions:info.permissions||[]}
}

export type DashboardResumo = {
  contas: ContaComSaldo[]
  recentes: Lancamento[]
  saldoDisponivel: number
  receitasAReceber: number
  despesasAPagar: number
  saldoFinalProjetado: number
  receitasRealizadas: number
  despesasRealizadas: number
  transferenciasRealizadas: number
  resultadoPeriodo: number
  atrasadas: number
  hojeValor: number
  proximos7: number
}

export function temPermissao(acesso: AcessoBase | null | undefined, permissao: string) {
  if (!acesso?.ativo) return false
  const permissoes = acesso.permissions || []
  if (permissoes.length) return permissoes.includes('*') || permissoes.includes(permissao)

  // Compatibilidade temporária enquanto telas ainda usam os perfis legados.
  if (acesso.perfil === 'ADMIN') return true
  if (acesso.perfil === 'LANCADOR') {
    const operacionais = [
      'dashboard.view',
      'lancamentos.view','lancamentos.create','lancamentos.edit',
      'transferencias.view','transferencias.create','transferencias.edit',
      'cartoes.view','cartoes.create','cartoes.edit',
      'investimentos.view','investimentos.create','investimentos.edit',
      'contas.view','plano_contas.view',
      'recorrencias.view','recorrencias.manage',
      'orcamentos.view','orcamentos.manage',
      'metas.view','metas.manage',
      'relatorios.view','fiscal.view',
      'conciliacao.view','conciliacao.manage',
      'configuracoes.view',
    ]
    return operacionais.includes(permissao)
  }
  if (acesso.perfil === 'CONSULTA') {
    const leitura = [
      'dashboard.view','lancamentos.view','transferencias.view','cartoes.view',
      'investimentos.view','contas.view','plano_contas.view','recorrencias.view',
      'orcamentos.view','metas.view','relatorios.view','fiscal.view',
      'conciliacao.view','configuracoes.view',
    ]
    return leitura.includes(permissao)
  }
  return false
}

export function podeEscrever(perfil?: PerfilAcesso | null) {
  return perfil === 'ADMIN' || perfil === 'LANCADOR'
}

export function podeExcluir(perfil?: PerfilAcesso | null) {
  return perfil === 'ADMIN'
}



export async function salvarConta(baseId: string, conta: Partial<Conta> & { id?: string }) {
  const payload = {
    base_id: baseId,
    instituicao: conta.instituicao?.trim() || null,
    nome: conta.nome?.trim() || null,
    tipo: conta.tipo?.trim() || null,
    saldo_inicial: Number(conta.saldo_inicial || 0),
    data_saldo_inicial: conta.data_saldo_inicial || null,
    principal: !!conta.principal,
    ativa: conta.ativa !== false,
    atualizado_em: new Date().toISOString(),
  }

  if (payload.principal) {
    const { error: principalError } = await dataClient
      .from('contas')
      .update({ principal: false, atualizado_em: new Date().toISOString() })
      .eq('base_id', baseId)
      .neq('id', conta.id || '00000000-0000-0000-0000-000000000000')
    if (principalError) throw principalError
  }

  if (conta.id) {
    const { error } = await dataClient.from('contas').update(payload).eq('id', conta.id).eq('base_id', baseId)
    if (error) throw error
    return
  }

  const { error } = await dataClient.from('contas').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export async function alterarStatusConta(baseId: string, id: string, ativa: boolean) {
  const { error } = await dataClient
    .from('contas')
    .update({ ativa, principal: ativa ? undefined : false, atualizado_em: new Date().toISOString() })
    .eq('id', id)
    .eq('base_id', baseId)
  if (error) throw error
}

export async function listarCategorias(baseId: string): Promise<Categoria[]> {
  const { data, error } = await dataClient
    .from('categorias')
    .select('*')
    .eq('base_id', baseId)
    .order('tipo', { ascending: true })
    .order('ordem', { ascending: true })
    .order('nome', { ascending: true })
  if (error) throw error
  return (data || []) as Categoria[]
}

export async function salvarCategoria(baseId: string, categoria: Partial<Categoria> & { id?: string }) {
  const payload = {
    base_id: baseId,
    nome: categoria.nome?.trim() || '',
    tipo: String(categoria.tipo || 'DESPESA').toUpperCase(),
    ativa: categoria.ativa !== false,
    ordem: Number(categoria.ordem || 999),
    atualizado_em: new Date().toISOString(),
  }
  if (!payload.nome) throw new Error('Informe o nome da categoria.')

  if (categoria.id) {
    const { error } = await dataClient.from('categorias').update(payload).eq('id', categoria.id).eq('base_id', baseId)
    if (error) throw error
    return
  }
  const { error } = await dataClient.from('categorias').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export async function alterarStatusCategoria(baseId: string, id: string, ativa: boolean) {
  const { error } = await dataClient
    .from('categorias')
    .update({ ativa, atualizado_em: new Date().toISOString() })
    .eq('id', id)
    .eq('base_id', baseId)
  if (error) throw error
}

export async function listarSubcategorias(baseId: string, categorias?: Categoria[]): Promise<Subcategoria[]> {
  const cats = categorias || await listarCategorias(baseId)
  const ids = cats.map((c) => c.id)
  if (!ids.length) return []

  const { data, error } = await dataClient
    .from('subcategorias')
    .select('*')
    .eq('base_id', baseId)
    .in('categoria_id', ids)
    .order('ordem', { ascending: true })
    .order('nome', { ascending: true })
  if (error) throw error
  return (data || []) as Subcategoria[]
}

export async function salvarSubcategoria(baseId: string, sub: Partial<Subcategoria> & { id?: string }) {
  if (!sub.categoria_id) throw new Error('Selecione a categoria.')
  if (!sub.nome?.trim()) throw new Error('Informe o nome da subcategoria.')

  const payload = {
    base_id: baseId,
    categoria_id: sub.categoria_id,
    nome: sub.nome.trim(),
    tipo: sub.tipo || null,
    ativa: sub.ativa !== false,
    ordem: Number(sub.ordem || 999),
    atualizado_em: new Date().toISOString(),
  }

  if (sub.id) {
    const { error } = await dataClient.from('subcategorias').update(payload).eq('id', sub.id)
    if (error) throw error
    return
  }
  const { error } = await dataClient.from('subcategorias').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export async function alterarStatusSubcategoria(id: string, ativa: boolean) {
  const { error } = await dataClient
    .from('subcategorias')
    .update({ ativa, atualizado_em: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

export interface FiltroLancamentos {
  tipo?: string
  status?: string
  inicio?: string
  fim?: string
  texto?: string
}

export async function listarLancamentos(baseId: string, filtro: FiltroLancamentos = {}, limite = 500): Promise<Lancamento[]> {
  let q = dataClient
    .from('lancamentos')
    .select('*')
    .eq('base_id', baseId)
    .order('vencimento', { ascending: false })
    .order('criado_em', { ascending: false })
    .limit(limite)

  if (filtro.tipo) q = q.eq('tipo', filtro.tipo.toUpperCase())
  if (filtro.status) q = q.eq('status', filtro.status.toUpperCase())
  if (filtro.inicio) q = q.gte('vencimento', filtro.inicio)
  if (filtro.fim) q = q.lte('vencimento', filtro.fim)
  if (filtro.texto?.trim()) q = q.ilike('descricao', `%${filtro.texto.trim()}%`)

  const { data, error } = await q
  if (error) throw error
  return (data || []) as Lancamento[]
}

export async function listarLancamentosRecentes(baseId: string): Promise<Lancamento[]> {
  return listarLancamentos(baseId, {}, 10)
}

export interface LancamentoForm {
  id?: string
  tipo: TipoLancamento
  descricao: string
  categoria_id?: string | null
  subcategoria_id?: string | null
  competencia?: string | null
  vencimento?: string | null
  valor_previsto: number
  status?: string
  conta_prevista_id?: string | null
  realizado?: boolean
  valor_realizado?: number
  data_realizacao?: string | null
  conta_realizada_id?: string | null
  codigo_barras?: string | null
  chave_pix?: string | null
  observacoes?: string | null
  pj?: boolean
}



export async function salvarDadosPagamentoLancamento(
  baseId: string,
  id: string,
  dados: { codigoBarras?: string | null; chavePix?: string | null },
) {
  const { error } = await dataClient
    .from('lancamentos')
    .update({
      codigo_barras: dados.codigoBarras?.trim() || null,
      chave_pix: dados.chavePix?.trim() || null,
      atualizado_em: new Date().toISOString(),
    })
    .eq('id', id)
    .eq('base_id', baseId)
  if (error) throw error
}

export async function salvarCodigoBarrasLancamento(baseId: string, id: string, codigoBarras?: string | null) {
  return salvarDadosPagamentoLancamento(baseId, id, { codigoBarras })
}



export async function baixarLancamento(
  baseId: string,
  id: string,
  tipo: TipoLancamento,
  contaId: string,
  data: string,
  valor: number,
) {
  if (!contaId) throw new Error('Selecione a conta da baixa.')
  if (!data) throw new Error('Informe a data da realização.')
  if (!(Number(valor) > 0)) throw new Error('Informe um valor realizado maior que zero.')

  const status = tipo === 'RECEITA' ? 'RECEBIDO' : 'PAGO'
  const { data: atualizado, error } = await dataClient
    .from('lancamentos')
    .update({
      status,
      conta_realizada_id: contaId,
      data_realizacao: data,
      valor_realizado: Math.abs(Number(valor)),
      atualizado_em: new Date().toISOString(),
    })
    .eq('id', id)
    .eq('base_id', baseId)
    .eq('status', 'PENDENTE')
    .eq('tipo', tipo)
    .select('id,tipo,status,conta_realizada_id,data_realizacao,valor_realizado')
    .maybeSingle()

  if (error) throw error
  if (!atualizado) throw new Error('Este lançamento já foi alterado ou realizado. Atualize a tela antes de tentar novamente.')
  if (atualizado.status !== status) throw new Error('A baixa foi gravada com status inesperado.')

  return atualizado
}

export async function excluirLancamento(baseId: string, id: string) {
  const { error } = await dataClient.from('lancamentos').delete().eq('id', id).eq('base_id', baseId)
  if (error) throw error
}


export async function listarTransferencias(baseId: string, limite = 500): Promise<Transferencia[]> {
  const { data, error } = await dataClient
    .from('transferencias')
    .select('*')
    .eq('base_id', baseId)
    .order('data_realizacao', { ascending: false, nullsFirst: false })
    .order('data_prevista', { ascending: false, nullsFirst: false })
    .limit(limite)
  if (error) throw error
  return (data || []) as Transferencia[]
}



export async function listarMovimentosInvestimentos(baseId: string): Promise<MovimentoInvestimento[]> {
  const { data, error } = await dataClient
    .from('mov_investimentos')
    .select('*')
    .eq('base_id', baseId)
    .order('data', { ascending: true })
    .order('criado_em', { ascending: true })
  if (error) throw error
  return (data || []) as MovimentoInvestimento[]
}

export async function listarPosicoesInvestimentos(baseId: string): Promise<PosicaoInvestimento[]> {
  const { data, error } = await dataClient
    .from('posicoes_investimentos')
    .select('*')
    .eq('base_id', baseId)
    .order('data_posicao', { ascending: true })
    .order('criado_em', { ascending: true })
  if (error) throw error
  return (data || []) as PosicaoInvestimento[]
}

function efeitoMovimentoInvestimento(tipo: string, valor: number) {
  const t = String(tipo || '').toUpperCase()
  const v = Math.abs(Number(valor || 0))
  if (t === 'APORTE' || t === 'RENDIMENTO') return v
  if (t === 'RESGATE' || t === 'IMPOSTO' || t === 'TARIFA') return -v
  return 0
}

export async function calcularSaldosInvestimentos(baseId: string): Promise<InvestimentoComSaldo[]> {
  const [investimentos, movimentos, posicoes] = await Promise.all([
    listarInvestimentos(baseId),
    listarMovimentosInvestimentos(baseId),
    listarPosicoesInvestimentos(baseId),
  ])

  return investimentos.map((inv) => {
    const mov = movimentos.filter((m) => m.investimento_id === inv.id)
    const pos = posicoes
      .filter((p) => p.investimento_id === inv.id)
      .sort((a, b) => String(a.data_posicao || '').localeCompare(String(b.data_posicao || '')))
    const ultima = pos[pos.length - 1]
    let saldo = ultima ? Number(ultima.saldo || 0) : Number(inv.valor_inicial || 0)
    const dataPos = ultima?.data_posicao || ''
    mov.forEach((m) => {
      if (!ultima || String(m.data || '') > dataPos) saldo += efeitoMovimentoInvestimento(m.tipo, m.valor)
    })
    return { ...inv, saldo_atual: Number(saldo.toFixed(2)) }
  })
}

export async function calcularSaldosContas(baseId: string): Promise<ContaComSaldo[]> {
  const [contas, lancamentos, transferencias, movimentosInvest] = await Promise.all([
    listarContas(baseId),
    dataClient.from('lancamentos').select('tipo,status,valor_realizado,conta_realizada_id').eq('base_id', baseId).in('status', ['PAGO', 'RECEBIDO']),
    dataClient.from('transferencias').select('status,origem_tipo,origem_conta_id,origem_investimento_id,destino_tipo,destino_conta_id,destino_investimento_id,valor').eq('base_id', baseId).eq('status', 'REALIZADA'),
    dataClient.from('mov_investimentos').select('tipo,valor,conta_id,transferencia_id').eq('base_id', baseId).not('conta_id', 'is', null),
  ])
  if (lancamentos.error) throw lancamentos.error
  if (transferencias.error) throw transferencias.error
  if (movimentosInvest.error) throw movimentosInvest.error

  const saldos = new Map<string, number>()
  contas.forEach((c) => {
    saldos.set(c.id, Number(c.saldo_inicial || 0))
  })

  ;(lancamentos.data || []).forEach((l) => {
    const id = String(l.conta_realizada_id || '')
    if (!saldos.has(id)) return
    const valor = Math.abs(Number(l.valor_realizado || 0))
    saldos.set(id, Number(saldos.get(id) || 0) + (String(l.tipo).toUpperCase() === 'RECEITA' ? valor : -valor))
  })

  ;(transferencias.data || []).forEach((t) => {
    const valor = Math.abs(Number(t.valor || 0))
    if (String(t.origem_tipo).toUpperCase() === 'CONTA') {
      const id = String(t.origem_conta_id || '')
      if (id && saldos.has(id)) saldos.set(id, Number(saldos.get(id) || 0) - valor)
    }
    if (String(t.destino_tipo).toUpperCase() === 'CONTA') {
      const id = String(t.destino_conta_id || '')
      if (id && saldos.has(id)) saldos.set(id, Number(saldos.get(id) || 0) + valor)
    }
  })

  ;(movimentosInvest.data || []).forEach((m) => {
    // Movimentos gerados por transferências já foram contabilizados acima.
    if (m.transferencia_id) return
    const id = String(m.conta_id || '')
    if (!saldos.has(id)) return
    const tipo = String(m.tipo || '').toUpperCase()
    const valor = Math.abs(Number(m.valor || 0))
    if (tipo === 'APORTE') saldos.set(id, Number(saldos.get(id) || 0) - valor)
    if (tipo === 'RESGATE') saldos.set(id, Number(saldos.get(id) || 0) + valor)
  })

  return contas.map((c) => ({ ...c, saldo_atual: Number(Number(saldos.get(c.id) || 0).toFixed(2)) }))
}

export async function obterExtratoConta(baseId: string, contaId: string, inicio?: string, fim?: string): Promise<{ itens: ExtratoContaItem[]; saldoPeriodo: number }> {
  const contas = await listarContas(baseId)
  const conta = contas.find((c) => c.id === contaId)
  if (!conta) throw new Error('Conta não localizada.')

  const [{ data: lanc, error: eLanc }, { data: trf, error: eTrf }, { data: movInv, error: eMovInv }] = await Promise.all([
    dataClient.from('lancamentos').select('id,tipo,descricao,status,data_realizacao,valor_realizado,conta_realizada_id').eq('base_id', baseId).eq('conta_realizada_id', contaId).in('status', ['PAGO', 'RECEBIDO']),
    dataClient.from('transferencias').select('id,data_realizacao,data_prevista,data,origem_tipo,origem_conta_id,origem_investimento_id,destino_tipo,destino_conta_id,destino_investimento_id,valor,observacao,status').eq('base_id', baseId).eq('status', 'REALIZADA'),
    dataClient.from('mov_investimentos').select('id,data,tipo,valor,conta_id,transferencia_id,observacao').eq('base_id', baseId).eq('conta_id', contaId),
  ])
  if (eLanc) throw eLanc
  if (eTrf) throw eTrf
  if (eMovInv) throw eMovInv

  const eventos: Array<Omit<ExtratoContaItem, 'saldo'>> = []
  ;(lanc || []).forEach((l) => {
    const valor = Math.abs(Number(l.valor_realizado || 0))
    const receita = String(l.tipo).toUpperCase() === 'RECEITA'
    eventos.push({ id: l.id, data: l.data_realizacao || '', tipo: 'LANCAMENTO', descricao: l.descricao || 'Lançamento', entrada: receita ? valor : 0, saida: receita ? 0 : valor })
  })
  ;(trf || []).forEach((t) => {
    const valor = Math.abs(Number(t.valor || 0))
    const origem = String(t.origem_tipo).toUpperCase() === 'CONTA' &&
      String(t.origem_conta_id || '') === contaId
    const destino = String(t.destino_tipo).toUpperCase() === 'CONTA' &&
      String(t.destino_conta_id || '') === contaId
    if (!origem && !destino) return
    eventos.push({
      id: t.id,
      data: t.data_realizacao || t.data_prevista || t.data || '',
      tipo: 'TRANSFERENCIA',
      descricao: t.observacao ? `Transferência · ${t.observacao}` : 'Transferência',
      entrada: destino ? valor : 0,
      saida: origem ? valor : 0,
    })
  })
  ;(movInv || []).forEach((m) => {
    // Evita duplicar movimentos já representados pela própria transferência.
    if (m.transferencia_id) return
    const tipo = String(m.tipo || '').toUpperCase()
    const valor = Math.abs(Number(m.valor || 0))
    if (tipo !== 'APORTE' && tipo !== 'RESGATE') return
    eventos.push({
      id: m.id,
      data: m.data || '',
      tipo: 'TRANSFERENCIA',
      descricao: m.observacao ? `Investimento · ${m.observacao}` : (tipo === 'APORTE' ? 'Aporte em investimento' : 'Resgate de investimento'),
      entrada: tipo === 'RESGATE' ? valor : 0,
      saida: tipo === 'APORTE' ? valor : 0,
    })
  })
  eventos.sort((a, b) => a.data.localeCompare(b.data) || a.id.localeCompare(b.id))
  let saldo = Number(conta.saldo_inicial || 0)
  const eventosComSaldo = eventos.map((e) => {
    saldo += e.entrada - e.saida
    return { ...e, saldo: Number(saldo.toFixed(2)) }
  })

  // O saldo precisa ser calculado com todo o histórico anterior. Somente depois
  // limitamos as linhas exibidas ao período global selecionado pelo usuário.
  const dataEvento = (valor: string) => String(valor || '').slice(0, 10)
  const saldoPeriodo = eventosComSaldo
    .filter((e) => !fim || dataEvento(e.data) <= fim)
    .at(-1)?.saldo ?? Number(conta.saldo_inicial || 0)
  const itens = eventosComSaldo
    .filter((e) => (!inicio || dataEvento(e.data) >= inicio) && (!fim || dataEvento(e.data) <= fim))
    .reverse()

  return { itens, saldoPeriodo: Number(Number(saldoPeriodo).toFixed(2)) }
}

export interface TransferenciaForm {
  id?: string
  data_prevista: string
  realizada: boolean
  data_realizacao?: string | null
  origem_tipo: 'CONTA' | 'INVESTIMENTO'
  origem_id: string
  destino_tipo: 'CONTA' | 'INVESTIMENTO'
  destino_id: string
  valor: number
  observacao?: string | null
}

export async function salvarTransferencia(baseId: string, form: TransferenciaForm) {
  const {data,error}=await dataClient.rpc<{id:string}>('salvar_transferencia_atomica',{base_id:baseId,form})
  if(error)throw error
  if(!data?.id)throw new Error('A gravação da transferência não foi confirmada.')
}

export async function realizarTransferencia(baseId: string, trf: Transferencia, data: string) {
  const contas = await listarContas(baseId)
  const investimentos = await listarInvestimentos(baseId)
  const rel = trf as Transferencia & {
    origem_conta_id?: string | null
    origem_investimento_id?: string | null
    destino_conta_id?: string | null
    destino_investimento_id?: string | null
  }

  const origem = trf.origem_tipo === 'CONTA'
    ? contas.find((c) => c.id === rel.origem_conta_id)
    : investimentos.find((i) => i.id === rel.origem_investimento_id)

  const destino = trf.destino_tipo === 'CONTA'
    ? contas.find((c) => c.id === rel.destino_conta_id)
    : investimentos.find((i) => i.id === rel.destino_investimento_id)

  if (!origem || !destino) throw new Error('Origem ou destino da transferência não localizado.')

  await salvarTransferencia(baseId, {
    id: trf.id,
    data_prevista: trf.data_prevista || trf.data || data,
    realizada: true,
    data_realizacao: data,
    origem_tipo: trf.origem_tipo as 'CONTA'|'INVESTIMENTO',
    origem_id: origem.id,
    destino_tipo: trf.destino_tipo as 'CONTA'|'INVESTIMENTO',
    destino_id: destino.id,
    valor: Number(trf.valor),
    observacao: trf.observacao,
  })
}

export async function excluirTransferencia(baseId: string, trf: Transferencia) {
  const {data,error}=await dataClient.rpc<{id:string}>('excluir_transferencia_atomica',{base_id:baseId,id:trf.id})
  if(error)throw error
  if(!data?.id)throw new Error('A exclusão da transferência não foi confirmada.')
}

export async function obterResumoDashboard(baseId: string, periodo: string = 'ALL'): Promise<DashboardResumo> {
  return apiJson(`/dashboard/summary?base_id=${encodeURIComponent(baseId)}&periodo=${encodeURIComponent(periodo)}`)
}

export async function listarAnosDisponiveis(baseId: string): Promise<string[]> {
  const data = await apiJson(`/meta/periods?base_id=${encodeURIComponent(baseId)}`)
  return Array.isArray(data?.years) ? data.years.map(String) : []
}

/* ========================================================================== */
/* v4.4.0 - Investimentos e recorrências                                      */
/* ========================================================================== */

export interface InvestimentoForm {
  id?: string
  instituicao?: string | null
  nome?: string | null
  tipo?: string | null
  data_inicial?: string | null
  valor_inicial: number
  indexador?: string | null
  taxa?: number | null
  vencimento?: string | null
  liquidez?: string | null
  status?: string | null
  observacao?: string | null
}

export async function salvarInvestimento(baseId: string, form: InvestimentoForm) {
  if (!form.nome?.trim()) throw new Error('Informe o nome da aplicação.')
  if (!(Number(form.valor_inicial) >= 0)) throw new Error('Informe um valor inicial válido.')
  const payload = {
    base_id: baseId,
    instituicao: form.instituicao?.trim() || null,
    nome: form.nome.trim(),
    tipo: form.tipo?.trim() || null,
    data_inicial: form.data_inicial || null,
    valor_inicial: Math.abs(Number(form.valor_inicial || 0)),
    indexador: form.indexador?.trim() || null,
    taxa: form.taxa === null || form.taxa === undefined || form.taxa === 0 ? Number(form.taxa || 0) : Number(form.taxa),
    vencimento: form.vencimento || null,
    liquidez: form.liquidez?.trim() || null,
    status: String(form.status || 'ATIVO').toUpperCase(),
    observacao: form.observacao?.trim() || null,
    atualizado_em: new Date().toISOString(),
  }
  if (form.id) {
    const { data, error } = await dataClient.from('investimentos').update(payload).eq('id', form.id).eq('base_id', baseId).select('id').single()
    if (error) throw error
    return data.id as string
  }
  const { data, error } = await dataClient.from('investimentos').insert({ ...payload, criado_em: new Date().toISOString() }).select('id').single()
  if (error) throw error
  return data.id as string
}

export type OrigemAplicacaoInicial =
  | { tipo: 'HISTORICO' }
  | { tipo: 'CONTA'; id: string }
  | { tipo: 'INVESTIMENTO'; id: string }

export async function criarInvestimentoComOrigem(
  baseId: string,
  form: InvestimentoForm,
  origem: OrigemAplicacaoInicial,
) {
  const valor = Math.abs(Number(form.valor_inicial || 0))

  if (origem.tipo === 'HISTORICO') {
    return salvarInvestimento(baseId, form)
  }

  if (!(valor > 0)) throw new Error('Informe um valor inicial maior que zero.')
  if (!origem.id) throw new Error('Selecione a origem do recurso.')

  const investimentoId = await salvarInvestimento(baseId, { ...form, valor_inicial: 0 })
  try {
    await salvarTransferencia(baseId, {
      data_prevista: form.data_inicial || new Date().toISOString().slice(0, 10),
      realizada: true,
      data_realizacao: form.data_inicial || new Date().toISOString().slice(0, 10),
      origem_tipo: origem.tipo,
      origem_id: origem.id,
      destino_tipo: 'INVESTIMENTO',
      destino_id: investimentoId,
      valor,
      observacao: `Aplicação inicial · ${form.nome?.trim() || 'Investimento'}`,
    })
    return investimentoId
  } catch (error) {
    // Evita deixar uma aplicação vazia caso a movimentação inicial falhe.
    await dataClient.from('investimentos').delete().eq('id', investimentoId).eq('base_id', baseId)
    throw error
  }
}

export async function alterarStatusInvestimento(baseId: string, id: string, status: string) {
  const { error } = await dataClient.from('investimentos').update({ status: String(status).toUpperCase(), atualizado_em: new Date().toISOString() }).eq('id', id).eq('base_id', baseId)
  if (error) throw error
}

export interface MovimentoInvestimentoForm {
  id?: string
  investimento_id: string
  data: string
  tipo: string
  valor: number
  conta_id?: string | null
  observacao?: string | null
}

export async function salvarMovimentoInvestimento(baseId: string, form: MovimentoInvestimentoForm) {
  if (!form.investimento_id) throw new Error('Selecione o investimento.')
  if (!form.data) throw new Error('Informe a data do movimento.')
  if (!(Number(form.valor) > 0)) throw new Error('Informe um valor maior que zero.')
  const tipo = String(form.tipo || '').toUpperCase()
  if (!['APORTE', 'RESGATE', 'RENDIMENTO', 'IMPOSTO', 'TARIFA'].includes(tipo)) throw new Error('Tipo de movimento inválido.')
  const payload = {
    base_id: baseId,
    investimento_id: form.investimento_id,
    data: form.data,
    tipo,
    valor: Math.abs(Number(form.valor)),
    conta_id: form.conta_id || null,
    observacao: form.observacao?.trim() || null,
  }
  if (form.id) {
    const { error } = await dataClient.from('mov_investimentos').update(payload).eq('id', form.id).eq('base_id', baseId)
    if (error) throw error
    return
  }
  const { error } = await dataClient.from('mov_investimentos').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export interface PosicaoInvestimentoForm {
  id?: string
  investimento_id: string
  data_posicao: string
  saldo: number
  aportes_acum?: number | null
  resgates_acum?: number | null
  rendimento_calculado?: number | null
}

export async function salvarPosicaoInvestimento(baseId: string, form: PosicaoInvestimentoForm) {
  if (!form.investimento_id) throw new Error('Selecione o investimento.')
  if (!form.data_posicao) throw new Error('Informe a data da posição.')
  if (!(Number(form.saldo) >= 0)) throw new Error('Informe um saldo válido.')
  const payload = {
    base_id: baseId,
    investimento_id: form.investimento_id,
    data_posicao: form.data_posicao,
    saldo: Number(form.saldo || 0),
    aportes_acum: form.aportes_acum ?? null,
    resgates_acum: form.resgates_acum ?? null,
    rendimento_calculado: form.rendimento_calculado ?? null,
  }
  if (form.id) {
    const { error } = await dataClient.from('posicoes_investimentos').update(payload).eq('id', form.id).eq('base_id', baseId)
    if (error) throw error
    return
  }
  const { error } = await dataClient.from('posicoes_investimentos').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export interface RecorrenciaForm {
  id?: string
  tipo_lancamento: TipoLancamento
  descricao: string
  categoria_id?: string | null
  subcategoria_id?: string | null
  valor: number
  frequencia: string
  dia_referencia?: number | null
  intervalo_dias?: number | null
  data_inicio: string
  data_fim?: string | null
  conta_id?: string | null
  pj?: boolean
  ativa?: boolean
  observacao?: string | null
}









export async function listarRecorrencias(baseId: string): Promise<import('../types/database').Recorrencia[]> {
  const { data, error } = await dataClient.from('recorrencias').select('*').eq('base_id', baseId).order('ativa', { ascending: false }).order('descricao', { ascending: true })
  if (error) throw error
  return (data || []) as import('../types/database').Recorrencia[]
}

export async function listarRecorrenciasGerenciais(baseId: string): Promise<import('../types/database').RecorrenciaGerencial[]> {
  const [recs, lanc] = await Promise.all([
    listarRecorrencias(baseId),
    dataClient.from('lancamentos').select('id,recorrencia_id,vencimento,status').eq('base_id', baseId).eq('status', 'PENDENTE').not('recorrencia_id', 'is', null),
  ])
  if (lanc.error) throw lanc.error
  return recs.map(r => {
    const pend = (lanc.data || []).filter(l => String(l.recorrencia_id || '') === r.id).sort((a, b) => String(a.vencimento || '').localeCompare(String(b.vencimento || '')))
    return { ...r, proximo_vencimento: pend[0]?.vencimento || null, proximo_lancamento_id: pend[0]?.id || null, qtd_pendentes: pend.length }
  }).sort((a, b) => Number(b.ativa) - Number(a.ativa) || String(a.proximo_vencimento || '9999').localeCompare(String(b.proximo_vencimento || '9999')))
}









// ============================================================================
// v4.5.0 - Cartões de crédito
// ============================================================================
export interface CartaoForm {
  id?: string
  nome: string
  bandeira?: string | null
  limite?: number
  dia_fechamento: number
  dia_vencimento: number
  conta_pagamento_id?: string | null
  ativo?: boolean
}

export interface CompraCartaoForm {
  cartao_id: string
  data_compra: string
  descricao: string
  valor_total: number
  quantidade_parcelas: number
  categoria_id?: string | null
  subcategoria_id?: string | null
  observacao?: string | null
  fatura_competencia?: string | null
}



export async function listarComprasCartao(baseId: string): Promise<import('../types/database').CompraCartao[]> {
  const todas: import('../types/database').CompraCartao[] = []
  for (let inicio = 0; ; inicio += 500) {
    const { data, error } = await dataClient.from('compras_cartao').select('*').eq('base_id', baseId)
      .order('data_compra', { ascending: false }).order('id', { ascending: true }).range(inicio, inicio + 499)
    if (error) throw error
    todas.push(...(data || []) as import('../types/database').CompraCartao[])
    if ((data || []).length < 500) return todas
  }
}

export async function listarParcelasCartao(baseId: string): Promise<import('../types/database').ParcelaCartao[]> {
  const todas: import('../types/database').ParcelaCartao[] = []
  for (let inicio = 0; ; inicio += 500) {
    const { data, error } = await dataClient.from('parcelas_cartao').select('*').eq('base_id', baseId)
      .order('id', { ascending: true }).range(inicio, inicio + 499)
    if (error) throw error
    todas.push(...(data || []) as import('../types/database').ParcelaCartao[])
    if ((data || []).length < 500) return todas
  }
}

export async function listarFaturas(baseId: string): Promise<import('../types/database').FaturaCartao[]> {
  const todas: import('../types/database').FaturaCartao[] = []
  for (let inicio = 0; ; inicio += 500) {
    const { data, error } = await dataClient.from('faturas').select('*').eq('base_id', baseId).neq('status', 'CANCELADA')
      .order('vencimento', { ascending: false }).order('id', { ascending: true }).range(inicio, inicio + 499)
    if (error) throw error
    todas.push(...(data || []) as import('../types/database').FaturaCartao[])
    if ((data || []).length < 500) return todas
  }
}

/** Busca as baixas por fatura_id e também pelo lancamento_id da fatura (legados).
 * Não exige permissão de escrita e não modifica faturas nem parcelas. */
export interface ResultadoCancelamentoFaturaCartao {
  fatura_cancelada: boolean
  lancamentos_cancelados: number
  parcelas_liberadas: number
  compras_liberadas: number
}

export async function cancelarFaturaCartao(
  baseId: string,
  faturaId: string,
): Promise<ResultadoCancelamentoFaturaCartao> {
  const { data, error } = await dataClient.rpc('cancelar_fatura_cartao', {
    p_base_id: baseId,
    p_fatura_id: faturaId,
  })
  if (error) throw new Error(`Falha ao excluir a fatura: ${error.message}`)
  const resultado = (Array.isArray(data) ? data[0] : data) as ResultadoCancelamentoFaturaCartao | null
  if (!resultado?.fatura_cancelada) throw new Error('O cancelamento da fatura não foi confirmado pelo banco.')
  return resultado
}

export async function listarPagamentosFaturasCartao(
  baseId: string,
  faturas: import('../types/database').FaturaCartao[],
): Promise<PagamentoFaturaCartao[]> {
  const ids = [...new Set(faturas.map(f => f.id).filter(Boolean))]
  const idsLancamento = [...new Set(faturas.map(f => f.lancamento_id).filter((id): id is string => !!id))]
  const campos = 'id,fatura_id,status,valor_realizado,data_realizacao,conta_realizada_id'
  const dividir = (valores: string[]) => Array.from({ length: Math.ceil(valores.length / 75) }, (_, i) => valores.slice(i * 75, i * 75 + 75))
  const consultas = [
    ...dividir(ids).map(lote => dataClient.from('lancamentos').select(campos).eq('base_id', baseId).in('fatura_id', lote)),
    ...dividir(idsLancamento).map(lote => dataClient.from('lancamentos').select(campos).eq('base_id', baseId).in('id', lote)),
  ]
  const resultados = await Promise.all(consultas)
  const relacionados = new Map<string, PagamentoFaturaCartao>()
  for (const resultado of resultados) {
    if (resultado.error) throw new Error(`Falha ao conferir as baixas de faturas: ${resultado.error.message}`)
    for (const l of resultado.data || []) relacionados.set(l.id, l as PagamentoFaturaCartao)
  }
  return [...relacionados.values()]
}

export async function salvarCartao(baseId: string, form: CartaoForm) {
  if (!form.nome.trim()) throw new Error('Informe o nome do cartão.')
  if (!(form.dia_fechamento >= 1 && form.dia_fechamento <= 31)) throw new Error('Informe um dia de fechamento válido.')
  if (!(form.dia_vencimento >= 1 && form.dia_vencimento <= 31)) throw new Error('Informe um dia de vencimento válido.')
  const payload = { base_id: baseId, nome: form.nome.trim(), bandeira: form.bandeira?.trim() || null, limite: Number(form.limite || 0), dia_fechamento: Number(form.dia_fechamento), dia_vencimento: Number(form.dia_vencimento), conta_pagamento_id: form.conta_pagamento_id || null, ativo: form.ativo !== false, atualizado_em: new Date().toISOString() }
  if (form.id) {
    const { error } = await dataClient.from('cartoes').update(payload).eq('id', form.id).eq('base_id', baseId)
    if (error) throw error
    return
  }
  const { error } = await dataClient.from('cartoes').insert({ ...payload, criado_em: new Date().toISOString() })
  if (error) throw error
}

export async function alterarStatusCartao(baseId: string, id: string, ativo: boolean) {
  const { error } = await dataClient.from('cartoes').update({ ativo, atualizado_em: new Date().toISOString() }).eq('id', id).eq('base_id', baseId)
  if (error) throw error
}

function vencimentoParcela(dataCompra: string, parcela: number, diaVencimento: number) {
  const [y, m] = dataCompra.slice(0, 10).split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + parcela, 1, 12))
  const ultimo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12)).getUTCDate()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(Math.min(diaVencimento, ultimo)).padStart(2,'0')}`
}















export interface AtualizarCompraCartaoForm extends CompraCartaoForm {
  id: string
}

function parcelaCartaoProtegida(parcela: { status?: string | null }) {
  return String(parcela.status || '').toUpperCase() === 'PAGA'
}








export type ModoExclusaoCompraCartao = 'APENAS_ATUAL' | 'ATUAL_E_FUTURAS' | 'TODAS'

export interface ExcluirCompraCartaoOpcoes {
  modo?: ModoExclusaoCompraCartao
  numeroParcelaReferencia?: number | null
}

export interface ResultadoExclusaoCompraCartao {
  parcelasExcluidas: number
  compraExcluida: boolean
  parcelasRestantes: number
}

/**
 * Exclui uma compra/parcela do cartão com validação efetiva do DELETE.
 *
 * Regras:
 * - parcela PAGA nunca pode ser excluída;
 * - parcela de fatura ABERTA pode ser removida da compra sem apagar a fatura;
 * - fatura/conta a pagar já paga bloqueia a exclusão;
 * - em compra parcelada pode excluir somente a parcela de referência ou ela e as futuras;
 * - se não restar nenhuma parcela, a compra também é excluída.
 */



export interface ItemFaturaCartaoForm {
  cartao_id: string
  data_compra: string
  descricao: string
  valor: number
  competencia?: string | null
  vencimento?: string | null
  observacao?: string | null
  fatura_id?: string | null
  categoria_id?: string | null
  subcategoria_id?: string | null
  numero_parcela?: number | null
  total_parcelas?: number | null
  gerar_parcelas_futuras?: boolean
  parcela_existente_id?: string | null
}

export interface FaturaImportadaForm {
  cartao_id: string
  competencia: string
  vencimento?: string | null
  valor: number
}







function adicionarMesesData(dataIso: string, quantidade: number) {
  const [ano, mes, dia] = dataIso.slice(0, 10).split('-').map(Number)
  const base = new Date(Date.UTC(ano, mes - 1 + quantidade, 1, 12))
  const ultimoDia = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0, 12)).getUTCDate()
  const d = Math.min(Math.max(1, dia || 1), ultimoDia)
  return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}



/**
 * Registra exatamente uma linha de fatura já fechada.
 * Diferente de salvarCompraCartao(), não expande parcelamento: a fatura já
 * representa a parcela cobrada naquele mês e expandi-la geraria duplicidade.
 */


// ============================================================================
// v4.6.0 - Gestão e análise
// ============================================================================
export async function listarTabelaGerencial(tabela: 'orcamentos'|'metas'|'conciliacao'|'prolabore', baseId: string) {
  const { data, error } = await dataClient.from(tabela).select('*').eq('base_id', baseId).limit(1000)
  if (error) throw error
  return (data || []) as Record<string, any>[]
}

export async function obterDadosAnaliticos(baseId: string) {
  const [lancamentos, categorias] = await Promise.all([
    listarLancamentos(baseId, {}, 5000),
    listarCategorias(baseId),
  ])
  return { lancamentos, categorias }
}


// ============================================================================
// v4.8.2 - Pró-labore e importação de conciliação
// ============================================================================
export interface ProLaboreForm {
  ano: number
  mes: number
  valor_liquido: number
  data_pagamento?: string | null
  observacao?: string | null
}

export async function salvarProLabore(baseId: string, form: ProLaboreForm) {
  const ano = Number(form.ano)
  const mes = Number(form.mes)
  if (!ano || mes < 1 || mes > 12) throw new Error('Competência inválida para o pró-labore.')
  const competencia = `${ano}-${String(mes).padStart(2, '0')}-01`
  const payload = {
    base_id: baseId,
    ano,
    mes,
    competencia,
    data_pagamento: form.data_pagamento || null,
    valor_liquido: Number(form.valor_liquido || 0),
    observacao: form.observacao?.trim() || null,
    atualizado_em: new Date().toISOString(),
  }

  const { data: existente, error: buscaErro } = await dataClient
    .from('prolabore')
    .select('id')
    .eq('base_id', baseId)
    .eq('ano', ano)
    .eq('mes', mes)
    .maybeSingle()
  if (buscaErro) throw buscaErro

  if (existente?.id) {
    const { error } = await dataClient.from('prolabore').update(payload).eq('id', existente.id).eq('base_id', baseId)
    if (error) throw error
    return existente.id as string
  }

  const { data, error } = await dataClient
    .from('prolabore')
    .insert({ ...payload, criado_em: new Date().toISOString() })
    .select('id')
    .single()
  if (error) throw error
  return data.id as string
}

export interface MovimentoConciliacaoImportado {
  data: string
  descricao: string
  documento?: string | null
  valor: number
  hash: string
}

export async function importarMovimentosConciliacao(
  baseId: string,
  contaId: string,
  arquivo: string,
  movimentos: MovimentoConciliacaoImportado[],
) {
  if (!contaId) throw new Error('Selecione a conta do extrato.')
  if (!movimentos.length) throw new Error('Nenhum movimento reconhecido no arquivo.')

  const hashes = movimentos.map(m => m.hash).filter(Boolean)
  const { data: existentes, error: buscaErro } = await dataClient
    .from('conciliacao')
    .select('hash')
    .eq('base_id', baseId)
    .in('hash', hashes.length ? hashes : ['__SEM_HASH__'])
  if (buscaErro) throw buscaErro

  const jaExiste = new Set((existentes || []).map((x:any) => String(x.hash || '')))
  const agora = new Date().toISOString()
  const novos = movimentos.filter(m => !jaExiste.has(m.hash)).map((m, i) => ({
    base_id: baseId,
    conta_id: contaId,
    arquivo,
    data: m.data,
    descricao: m.descricao,
    documento: m.documento || null,
    valor: Number(m.valor),
    tipo: Number(m.valor) >= 0 ? 'CRÉDITO' : 'DÉBITO',
    status: 'PENDENTE',
    lancamento_id: null,
    hash: m.hash,
    importado_em: agora,
    conciliado_em: null,
  }))

  if (novos.length) {
    const { error } = await dataClient.from('conciliacao').insert(novos)
    if (error) throw error
  }

  return { total: movimentos.length, importados: novos.length, duplicados: movimentos.length - novos.length }
}


// ============================================================================
// v4.10.0 - Transferências recorrentes
// ============================================================================
export interface TransferenciaRecorrenciaForm {
  origem_tipo:'CONTA'|'INVESTIMENTO'
  origem_id:string
  destino_tipo:'CONTA'|'INVESTIMENTO'
  destino_id:string
  valor:number
  observacao?:string|null
  frequencia:string
  intervalo_dias?:number|null
  data_inicio:string
  data_fim?:string|null
}






// ============================================================================
// v4.10.20 - Relacionamentos operacionais exclusivamente por UUID
// ============================================================================
// origem_legacy_id, destino_legacy_id e transferencia_legacy_id deixam de ser
// usados e gravados nos fluxos novos. Os legacy_id próprios dos registros
// permanecem temporariamente para compatibilidade e auditoria histórica.

// ============================================================================
// v4.10.24 - UUID como identificador nativo definitivo
// ============================================================================
// Novos registros deixam de receber legacy_id artificiais "*-V4-*". Os valores
// legados já existentes permanecem preservados no banco para histórico e
// auditoria. Relacionamentos operacionais continuam exclusivamente por UUID.

// ============================================================================
// v4.10.81 - Central de conciliação inteligente
// ============================================================================
export interface CriarLancamentoConciliacaoForm {
  descricao?: string | null
  categoria_id?: string | null
  subcategoria_id?: string | null
  observacoes?: string | null
}

export async function conciliarMovimentoComLancamento(baseId: string, movimentoId: string, lancamentoId: string) {
  const { data: movimento, error: movErro } = await dataClient
    .from('conciliacao')
    .select('id,base_id,conta_id,data,descricao,documento,valor,status,lancamento_id')
    .eq('id', movimentoId)
    .eq('base_id', baseId)
    .single()
  if (movErro || !movimento) throw new Error(`Movimento bancário não localizado${movErro?.message ? `: ${movErro.message}` : '.'}`)

  if (String(movimento.status || '').toUpperCase() === 'CONCILIADO') {
    if (String(movimento.lancamento_id || '') === lancamentoId) return lancamentoId
    throw new Error('Este movimento já está conciliado com outro lançamento.')
  }

  const { data: lancamento, error: lancErro } = await dataClient
    .from('lancamentos')
    .select('*')
    .eq('id', lancamentoId)
    .eq('base_id', baseId)
    .single()
  if (lancErro || !lancamento) throw new Error(`Lançamento não localizado${lancErro?.message ? `: ${lancErro.message}` : '.'}`)

  const valor = Number(movimento.valor || 0)
  const tipoEsperado = valor >= 0 ? 'RECEITA' : 'DESPESA'
  if (String(lancamento.tipo || '').toUpperCase() !== tipoEsperado) {
    throw new Error(`O movimento é ${tipoEsperado === 'RECEITA' ? 'um crédito' : 'um débito'} e não pode ser conciliado com esse tipo de lançamento.`)
  }
  if (['CANCELADO'].includes(String(lancamento.status || '').toUpperCase())) {
    throw new Error('Não é possível conciliar um lançamento cancelado.')
  }

  const backup = {
    status: lancamento.status,
    valor_realizado: lancamento.valor_realizado,
    data_realizacao: lancamento.data_realizacao,
    conta_realizada_id: lancamento.conta_realizada_id,
    atualizado_em: lancamento.atualizado_em,
  }
  const agora = new Date().toISOString()
  const payloadLancamento = {
    status: tipoEsperado === 'RECEITA' ? 'RECEBIDO' : 'PAGO',
    valor_realizado: Math.abs(valor),
    data_realizacao: movimento.data,
    conta_realizada_id: movimento.conta_id,
    atualizado_em: agora,
  }

  const { error: updLancErro } = await dataClient
    .from('lancamentos')
    .update(payloadLancamento)
    .eq('id', lancamentoId)
    .eq('base_id', baseId)
  if (updLancErro) throw new Error(`Falha ao baixar o lançamento: ${updLancErro.message}`)

  const { error: updMovErro } = await dataClient
    .from('conciliacao')
    .update({ status: 'CONCILIADO', lancamento_id: lancamentoId, conciliado_em: agora })
    .eq('id', movimentoId)
    .eq('base_id', baseId)

  if (updMovErro) {
    await dataClient.from('lancamentos').update(backup).eq('id', lancamentoId).eq('base_id', baseId)
    throw new Error(`Falha ao concluir a conciliação: ${updMovErro.message}`)
  }

  return lancamentoId
}

export async function criarLancamentoAPartirConciliacao(baseId: string, movimentoId: string, form: CriarLancamentoConciliacaoForm = {}) {
  const { data: movimento, error: movErro } = await dataClient
    .from('conciliacao')
    .select('id,base_id,conta_id,data,descricao,documento,valor,status,lancamento_id,arquivo')
    .eq('id', movimentoId)
    .eq('base_id', baseId)
    .single()
  if (movErro || !movimento) throw new Error(`Movimento bancário não localizado${movErro?.message ? `: ${movErro.message}` : '.'}`)
  if (String(movimento.status || '').toUpperCase() === 'CONCILIADO') throw new Error('Este movimento já está conciliado.')

  const valor = Number(movimento.valor || 0)
  if (!Number.isFinite(valor) || Math.abs(valor) < 0.0001) throw new Error('O movimento não possui valor válido.')
  const tipo = valor >= 0 ? 'RECEITA' : 'DESPESA'
  const agora = new Date().toISOString()
  const obs = [
    form.observacoes?.trim(),
    `Criado pela conciliação bancária${movimento.arquivo ? ` a partir de ${movimento.arquivo}` : ''}.`,
    movimento.documento ? `Documento: ${movimento.documento}` : null,
  ].filter(Boolean).join(' ')

  const { data: novo, error: insErro } = await dataClient
    .from('lancamentos')
    .insert({
      base_id: baseId,
      tipo,
      descricao: String(form.descricao || movimento.descricao || 'Movimento bancário').trim(),
      categoria_id: form.categoria_id || null,
      subcategoria_id: form.subcategoria_id || null,
      competencia: `${String(movimento.data).slice(0,7)}-01`,
      vencimento: movimento.data,
      valor_previsto: Math.abs(valor),
      valor_realizado: Math.abs(valor),
      status: tipo === 'RECEITA' ? 'RECEBIDO' : 'PAGO',
      data_realizacao: movimento.data,
      conta_prevista_id: movimento.conta_id || null,
      conta_realizada_id: movimento.conta_id || null,
      observacoes: obs || null,
      pj: false,
      origem: 'CONCILIACAO',
      criado_em: agora,
      atualizado_em: agora,
    })
    .select('id')
    .single()
  if (insErro || !novo) throw new Error(`Falha ao criar o lançamento: ${insErro?.message || 'registro não retornado.'}`)

  const { error: updErro } = await dataClient
    .from('conciliacao')
    .update({ status: 'CONCILIADO', lancamento_id: novo.id, conciliado_em: agora })
    .eq('id', movimentoId)
    .eq('base_id', baseId)
  if (updErro) {
    await dataClient.from('lancamentos').delete().eq('id', novo.id).eq('base_id', baseId)
    throw new Error(`Falha ao concluir a conciliação: ${updErro.message}`)
  }

  return novo.id as string
}


/** v4.10.83 - Auditoria e histórico de alterações. */
export async function listarAuditoria(baseId:string,limite=1000):Promise<AuditoriaEvento[]>{
  const {data,error}=await dataClient.from('audit_events').select('*').eq('base_id',baseId).order('criado_em',{ascending:false}).limit(Math.min(Math.max(limite,1),5000))
  if(error)throw error
  return ((data||[]) as any[]).map((item)=>({...item,tabela:item.entidade,usuario_id:item.user_id})) as AuditoriaEvento[]
}



const localOperations=createFinancialOperations(dataClient)
export async function salvarFaturaImportada(...args:Parameters<typeof localOperations.salvarFaturaImportada>):Promise<Awaited<ReturnType<typeof localOperations.salvarFaturaImportada>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarFaturaImportada>>>('financial_transaction',{operation:'salvarFaturaImportada',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarFaturaImportada>>
}
export const listarCartoes=localOperations.listarCartoes
export async function salvarItemFaturaCartao(...args:Parameters<typeof localOperations.salvarItemFaturaCartao>):Promise<Awaited<ReturnType<typeof localOperations.salvarItemFaturaCartao>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarItemFaturaCartao>>>('financial_transaction',{operation:'salvarItemFaturaCartao',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarItemFaturaCartao>>
}
export async function sincronizarFaturasAbertasContasPagar(...args:Parameters<typeof localOperations.sincronizarFaturasAbertasContasPagar>):Promise<Awaited<ReturnType<typeof localOperations.sincronizarFaturasAbertasContasPagar>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.sincronizarFaturasAbertasContasPagar>>>('financial_transaction',{operation:'sincronizarFaturasAbertasContasPagar',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.sincronizarFaturasAbertasContasPagar>>
}
export async function salvarLancamento(...args:Parameters<typeof localOperations.salvarLancamento>):Promise<Awaited<ReturnType<typeof localOperations.salvarLancamento>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarLancamento>>>('financial_transaction',{operation:'salvarLancamento',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarLancamento>>
}
export async function salvarCompraCartao(...args:Parameters<typeof localOperations.salvarCompraCartao>):Promise<Awaited<ReturnType<typeof localOperations.salvarCompraCartao>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarCompraCartao>>>('financial_transaction',{operation:'salvarCompraCartao',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarCompraCartao>>
}
export async function atualizarCompraCartao(...args:Parameters<typeof localOperations.atualizarCompraCartao>):Promise<Awaited<ReturnType<typeof localOperations.atualizarCompraCartao>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.atualizarCompraCartao>>>('financial_transaction',{operation:'atualizarCompraCartao',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.atualizarCompraCartao>>
}
export async function gerarParcelasAdicionaisCompraCartao(...args:Parameters<typeof localOperations.gerarParcelasAdicionaisCompraCartao>):Promise<Awaited<ReturnType<typeof localOperations.gerarParcelasAdicionaisCompraCartao>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.gerarParcelasAdicionaisCompraCartao>>>('financial_transaction',{operation:'gerarParcelasAdicionaisCompraCartao',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.gerarParcelasAdicionaisCompraCartao>>
}
export async function moverParcelaCartaoParaFatura(...args:Parameters<typeof localOperations.moverParcelaCartaoParaFatura>):Promise<Awaited<ReturnType<typeof localOperations.moverParcelaCartaoParaFatura>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.moverParcelaCartaoParaFatura>>>('financial_transaction',{operation:'moverParcelaCartaoParaFatura',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.moverParcelaCartaoParaFatura>>
}
export async function excluirCompraCartao(...args:Parameters<typeof localOperations.excluirCompraCartao>):Promise<Awaited<ReturnType<typeof localOperations.excluirCompraCartao>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.excluirCompraCartao>>>('financial_transaction',{operation:'excluirCompraCartao',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.excluirCompraCartao>>
}
export async function salvarRecorrencia(...args:Parameters<typeof localOperations.salvarRecorrencia>):Promise<Awaited<ReturnType<typeof localOperations.salvarRecorrencia>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarRecorrencia>>>('financial_transaction',{operation:'salvarRecorrencia',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarRecorrencia>>
}
export async function sincronizarRecorrencias(...args:Parameters<typeof localOperations.sincronizarRecorrencias>):Promise<Awaited<ReturnType<typeof localOperations.sincronizarRecorrencias>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.sincronizarRecorrencias>>>('financial_transaction',{operation:'sincronizarRecorrencias',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.sincronizarRecorrencias>>
}
export async function encerrarRecorrencia(...args:Parameters<typeof localOperations.encerrarRecorrencia>):Promise<Awaited<ReturnType<typeof localOperations.encerrarRecorrencia>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.encerrarRecorrencia>>>('financial_transaction',{operation:'encerrarRecorrencia',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.encerrarRecorrencia>>
}
export async function reativarRecorrencia(...args:Parameters<typeof localOperations.reativarRecorrencia>):Promise<Awaited<ReturnType<typeof localOperations.reativarRecorrencia>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.reativarRecorrencia>>>('financial_transaction',{operation:'reativarRecorrencia',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.reativarRecorrencia>>
}
export async function atualizarLancamentoRecorrenteFuturos(...args:Parameters<typeof localOperations.atualizarLancamentoRecorrenteFuturos>):Promise<Awaited<ReturnType<typeof localOperations.atualizarLancamentoRecorrenteFuturos>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.atualizarLancamentoRecorrenteFuturos>>>('financial_transaction',{operation:'atualizarLancamentoRecorrenteFuturos',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.atualizarLancamentoRecorrenteFuturos>>
}
export async function salvarTransferenciaRecorrente(...args:Parameters<typeof localOperations.salvarTransferenciaRecorrente>):Promise<Awaited<ReturnType<typeof localOperations.salvarTransferenciaRecorrente>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.salvarTransferenciaRecorrente>>>('financial_transaction',{operation:'salvarTransferenciaRecorrente',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.salvarTransferenciaRecorrente>>
}
export const listarContas=localOperations.listarContas
export const listarInvestimentos=localOperations.listarInvestimentos
export async function sincronizarTransferenciasRecorrentes(...args:Parameters<typeof localOperations.sincronizarTransferenciasRecorrentes>):Promise<Awaited<ReturnType<typeof localOperations.sincronizarTransferenciasRecorrentes>>>{
 const {data,error}=await dataClient.rpc<Awaited<ReturnType<typeof localOperations.sincronizarTransferenciasRecorrentes>>>('financial_transaction',{operation:'sincronizarTransferenciasRecorrentes',args})
 if(error)throw error
 return data as Awaited<ReturnType<typeof localOperations.sincronizarTransferenciasRecorrentes>>
}
const faturaCartaoQuitada=localOperations.faturaCartaoQuitada
const sincronizarLancamentoFaturaCartao=localOperations.sincronizarLancamentoFaturaCartao
const descricaoFaturaCartao=localOperations.descricaoFaturaCartao
const competenciaAdicionarMeses=localOperations.competenciaAdicionarMeses
const obterOuCriarFaturaAbertaCartao=localOperations.obterOuCriarFaturaAbertaCartao
const vencimentoDaCompetencia=localOperations.vencimentoDaCompetencia
const ajustarValorFaturaCartao=localOperations.ajustarValorFaturaCartao
const validarParcelasAlteraveis=localOperations.validarParcelasAlteraveis
const parseISOData=localOperations.parseISOData
const isoHojeLocal=localOperations.isoHojeLocal
const adicionarMeses=localOperations.adicionarMeses
const dataISO=localOperations.dataISO
const proximaDataRecorrencia=localOperations.proximaDataRecorrencia
const adicionarDias=localOperations.adicionarDias
const adicionarAnos=localOperations.adicionarAnos

export async function salvarLancamentoRecorrente(baseId:string,current:Lancamento,form:LancamentoForm){
 const {data,error}=await dataClient.rpc<string>('financial_transaction',{operation:'salvarLancamentoRecorrente',args:[baseId,current,form]})
 if(error)throw error
 return data as string
}
